import {view, css, Icon, Link} from '@sail/ui/deprecated';
import {support} from '@sail/icons/react/Icon';
import {document, api, list} from '@sail/icons/react/Icon__xsmall';
import {useAnalytics} from '@stripe-internal/inline-data';
import {IconAsset} from '@sail/icons/types';
import {
  Conversation,
  ConversationArticle,
} from '@stripe-internal/conversations-client';
import {useCallback} from 'react';

// textColor refers to the color of the predefined text displayed before the link.
// articleLinkColor is the color for the main article link placeholder

type SourceLinkPrefix = {
  icon: IconAsset;
  iconColor: string;
  text?: string;
  textColor?: string;
  articleLinkColor?: string;
};

const collectionToPrefix = new Map<string, SourceLinkPrefix>(
  Object.entries({
    support_docs: {
      icon: support,
      iconColor: '$color.brand500',
    },
    express_support_docs: {
      icon: support,
      iconColor: '$color.brand500',
    },
    support_topic_pages: {
      icon: list,
      iconColor: '$color.info300',
      text: 'Topic: ',
      textColor: '$color.info600',
    },
    link_support_docs: {
      icon: support,
      iconColor: '#00C767', // Link brand300
      articleLinkColor: '#006635',
    },
    stripe_docs: {
      icon: document,
      iconColor: '$hue.blue500',
      text: 'Docs: ',
      textColor: '$hue.blue600',
    },
    stripe_api_docs: {
      icon: api,
      iconColor: '$color.brand500',
      text: 'API: ',
      textColor: '$color.brand500',
    },
  }),
);

// Used if we can't find a match for the collection or a collection isn't returned by the backend.
const defaultPrefix = {
  icon: support,
  iconColor: '$color.brand500',
};

export const ArticleSourcesDropdownLink = ({
  conversation,
  article,
}: {
  conversation: Conversation;
  article: ConversationArticle;
}) => {
  const analytics = useAnalytics();

  const onPress = useCallback(() => {
    analytics.action('presented_article_clicked', {
      articleUrl: article.url,
    });
    conversation.action({name: 'article-visited', data: article});
  }, [analytics, article, conversation]);

  const prefix: SourceLinkPrefix = article.collection
    ? collectionToPrefix.get(article.collection) || defaultPrefix
    : defaultPrefix;

  return (
    <view.div
      css={{
        stack: 'x',
        alignY: 'center',
        alignX: 'start',
        gap: 'small',
      }}
    >
      <Icon
        icon={prefix.icon}
        size="xsmall"
        css={{
          color: css.value(prefix.iconColor),
          minWidth: css.value('12px'),
        }}
      />
      <Link
        href={article.url}
        target={article.url}
        onPress={onPress}
        css={{
          fontFamily: css.value('label.medium'),
          fontSize: css.value('12px'),
          color: 'primary',
          fontWeight: css.value('600'),
          overflow: 'hidden',
          whiteSpace: css.value('nowrap'),
          textOverflow: css.value('ellipsis'),
        }}
      >
        <view.span css={{color: css.value(prefix.textColor || 'primary')}}>
          {prefix.text}
        </view.span>
        <view.span
          css={{color: css.value(prefix.articleLinkColor || 'primary')}}
        >
          {article.title}
        </view.span>
      </Link>
    </view.div>
  );
};
